import { useReducer, useCallback } from 'react';
import {
  Ref,
  SurveyFormatState,
  SurveyFormatType,
  UseSurveyFormatOptions,
  UseSurveyFormatReturn,
  PayloadAction,
} from './types';
import { SurveyAction, SurveyFormat } from './constants';
import * as actions from './actions';
import { SurveyPayloadAction } from './actions';
import { findFirstFocusable, scrollIntoView } from './utils';

const stateReducer = (state: SurveyFormatState, action: SurveyPayloadAction): SurveyFormatState => {
  switch (action.type) {
    case SurveyAction.REGISTER: {
      return {
        ...state,
        views: [...(state.views ?? []), ...(action.payload === null ? [] : [action.payload])],
      };
    }
    case SurveyAction.UNREGISTER: {
      const views = Array.from(state.views);
      const refIdx = views.findIndex(ref => ref.name === action.payload);
      views.splice(refIdx, 1);
      return { ...state, views };
    }
    case SurveyAction.SET_ACTIVE: {
      const ref = state.views.find(view => view.name === action.payload) || null;
      return { ...state, active: ref };
    }
    case SurveyAction.SET_FORMAT: {
      return { ...state, format: action.payload };
    }
    default: {
      // throw or return state?
      throw new Error(`Unhandled SurveyFormatAction type: ${(action as PayloadAction<string, unknown>).type}`);
    }
  }
};

export default function useSurveyFormat(props?: UseSurveyFormatOptions): UseSurveyFormatReturn {
  const [state, dispatch] = useReducer(stateReducer, {
    views: [],
    format: props?.format || SurveyFormat.ONE_QUESTION_AT_A_TIME,
    active: null,
  });

  const register = useCallback((name: string, ref: Ref): void => dispatch(actions.register({ name, ref })), []);
  const unregister = useCallback((name: string): void => dispatch(actions.unregister(name)), []);
  const setFormat = useCallback((format: SurveyFormatType) => dispatch(actions.setFormat(format)), []);
  const scrollToActive = useCallback(
    (name: string) => {
      const view = state.views.find(v => v.name === name);
      if (view?.ref?.current) {
        scrollIntoView(view.ref.current);
      }
    },
    [state.views]
  );
  const setActive = useCallback(
    (name: string | null, direct?: boolean) => {
      if (direct) {
        dispatch(actions.setActive(name));
      } else if (name !== null) {
        scrollToActive(name);
      }
    },
    [scrollToActive]
  );

  const setFocus = useCallback(
    (name: string) => {
      const view = state.views.find(v => v.name === name);
      if (view?.ref?.current) {
        const firstFocusable = findFirstFocusable(view.ref.current);
        firstFocusable?.focus();
      }
    },
    [state]
  );
  const setNextActive = useCallback(() => {
    const idx = state.views.findIndex(v => v.name === state.active?.name);
    const nextActive = state.views[idx + 1] || null;
    if (nextActive?.ref?.current) {
      setFocus(nextActive.name);
    }
  }, [state, setFocus]);

  const stateGetters = {
    get views() {
      return state.views;
    },
    get format() {
      return state.format;
    },
    get active() {
      return state.active;
    },
  };

  const _api = {
    register,
    unregister,
    setFormat,
    setActive,
    setNextActive,
    setFocus,
    ...stateGetters,
  };

  return _api;
}
